import React from 'react';
import { type FormikValues } from 'formik';
import dayjs from 'dayjs';
import { Box, Stack, Typography } from '@mui/material';
import type { ReturnedComponentProps } from './types';
import {
  TextField,
  Select,
  DefaultSelect,
  DatePicker,
  Checkbox,
  type SelectOption,
  PhoneTextFiled,
} from '../../../../components';
import { checkInvalidDate } from '../../graphql/ApplicationForm/checkInvalidDate';

export const returnedComponent = <T extends FormikValues>({
  item,
  formik,
}: ReturnedComponentProps<T>): React.ReactNode => {
  const {
    name,
    label,
    type,
    options,
    required,
    minDate,
    maxDate,
    disabled,
    placeholder,
    clearable,
    maxCharacters,
    handleChange,
    company,
  } = item;
  const handleFieldChange = handleChange ?? formik.handleChange;

  const splitPath = name.split('.');
  const touched = splitPath.reduce((acc: any, part: string) => {
    // Check if the accumulator is truthy and has the property 'part'
    // to avoid accessing properties of undefined
    return acc !== undefined && acc[part] !== undefined ? acc[part] : undefined;
  }, formik.touched);
  const error = splitPath.reduce((acc: any, part: string) => {
    return acc !== undefined && acc[part] !== undefined ? acc[part] : undefined;
  }, formik.errors);
  const value = splitPath.reduce((acc: any, part: string) => {
    return acc !== undefined && acc[part] !== undefined ? acc[part] : undefined;
  }, formik.values);

  switch (type) {
    case 'text':
      return (
        <TextField
          InputLabelProps={{ shrink: true }}
          className="w-full"
          sx={{
            '.MuiInputBase-root': {
              ...(!disabled && { background: '#ffffff' }),
            },
          }}
          disabled={disabled}
          label={label}
          required={required}
          name={item.name}
          value={value}
          onChange={handleFieldChange}
          onBlur={formik.handleBlur}
          placeholder={placeholder}
          error={(touched as boolean) && Boolean(error as string)}
          helperText={
            (touched as React.ReactNode as boolean) &&
            (error as React.ReactNode)
          }
        />
      );
    case 'textarea':
      return (
        <>
          {maxCharacters !== undefined && (
            <Box textAlign={'right'}>
              <Typography variant="caption" color="textSecondary">
                Remaining characters:{' '}
                {value != null ? maxCharacters - value.length : maxCharacters}
              </Typography>
            </Box>
          )}
          <TextField
            disabled={disabled}
            className="w-full"
            type={type}
            required={required}
            label={label}
            name={name}
            multiline
            minRows={4}
            placeholder={placeholder}
            value={value}
            InputLabelProps={{ shrink: true }}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            error={(touched as boolean) && Boolean(error as string)}
            helperText={
              (touched as React.ReactNode as boolean) &&
              (error as React.ReactNode)
            }
          />
        </>
      );
    case 'select':
      return (
        <Stack direction={'row'}>
          <Select
            InputLabelProps={{ shrink: true }}
            clearable={clearable}
            className="w-full"
            sx={{
              '.MuiInputBase-root': {
                ...(!disabled && { background: '#ffffff' }),
              },
            }}
            disabled={disabled}
            options={options as SelectOption[]}
            label={label}
            required={required}
            name={name}
            value={value}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            error={(touched as boolean) && Boolean(error)}
            helperText={
              (touched as React.ReactNode as boolean) &&
              (error as React.ReactNode)
            }
          />
          <>
            {company?.component?.(
              company?.isPresent ?? false,
              company?.disabled ?? true,
            )}
          </>
        </Stack>
      );
    case 'default select':
      return (
        <DefaultSelect
          className="w-full"
          label={label}
          type={type}
          name={name}
          disabled={disabled}
          required={required}
          value={formik.values[name]}
          onChange={handleFieldChange}
          fullWidth
          onBlur={formik.handleBlur}
          InputLabelProps={{ shrink: true }}
          error={(touched as boolean) && Boolean(error)}
          helperText={
            (touched as React.ReactNode as boolean) &&
            (error as React.ReactNode)
          }
          options={options as SelectOption[]}
        />
      );
    case 'phone':
      return (
        <PhoneTextFiled
          country={'hk'}
          onChange={
            handleFieldChange as (e: string | React.ChangeEvent<any>) => void
          }
          containerStyle={{
            marginRight: '2%',
            width: '100%',
          }}
          inputProps={{
            required,
            name,
            autoFocus: false,
          }}
          onBlur={formik.handleBlur}
          helperText={error as string | undefined}
          specialLabel={label as string}
          disabled={disabled}
          value={formik.values[name]}
          error={(touched as boolean) && Boolean(error)}
        />
      );
    case 'date':
      return (
        <DatePicker
          name={name}
          className="w-full"
          sx={{
            '.MuiInputBase-root': {
              ...(!disabled && { background: '#ffffff' }),
            },
          }}
          required={required}
          label={label}
          minDate={dayjs(minDate)}
          maxDate={dayjs(maxDate)}
          InputLabelProps={{ shrink: true }}
          value={
            checkInvalidDate(value)
              ? null
              : dayjs(formik.values[name] as string)
          }
          disabled={disabled}
          onChange={date => {
            void formik.setFieldValue(name, date);
          }}
          onBlur={formik.handleBlur}
          error={(touched as boolean) && Boolean(error)}
          helperText={
            (touched as React.ReactNode as boolean) &&
            (error as React.ReactNode)
          }
          localTime={false}
        />
      );
    case 'checkbox':
      return (
        <span>
          <Checkbox
            name={name}
            label={<div className="!mt-2">{label}</div>}
            required={required}
            value={value}
            onChange={handleFieldChange}
            onBlur={formik.handleBlur}
            error={(touched as boolean) && Boolean(error)}
            disabled={disabled}
            sx={{
              alignItems: 'flex-start',
            }}
          />
        </span>
      );
    default:
      return <div />;
  }
};
